<script setup lang="ts">
// 色域指定: サンプル色 + 許容量でソフト選択。プレビュー (白黒) をクリックしてサンプル色を取る。
import { computed, onMounted, ref, watch } from 'vue'
import { useDocumentStore } from '@/stores/documentStore'
import { useUiStore } from '@/stores/uiStore'
import { useModal } from '@/composables/useModalStack'
import { useI18n } from '@/i18n'
import { runCommand } from '@/commands/registry'
import { readComposite } from '@/app/engine'
import { colorRange } from '@/selection/colorRange'
import { hexToRgb, rgbToHex } from '@/color/convert'
import { pixelStore } from '@/pixels/pixelStore'
import SliderField from '@/components/common/SliderField.vue'

const emit = defineEmits<{ close: [] }>()
useModal('color-range')
const { t } = useI18n()
const docStore = useDocumentStore()
const ui = useUiStore()
const hex = ref(ui.foreground)
const fuzziness = ref(40)
const invert = ref(false)
const sampleAll = ref(true)
const canvas = ref<HTMLCanvasElement | null>(null)
const doc = docStore.doc!
const MAX = 320
const scale = Math.min(1, MAX / Math.max(doc.meta.width, doc.meta.height))
const pw = Math.max(1, Math.round(doc.meta.width * scale)), ph = Math.max(1, Math.round(doc.meta.height * scale))

function reference(): { rgba: Uint8ClampedArray; width: number; height: number } | null {
  if (sampleAll.value) return readComposite()
  const id = doc.activeLayerIds[0]
  const layer = id ? docStore.rasterLayer(id) : undefined
  const s = layer ? pixelStore.get(layer.pixelId) : undefined
  if (!layer || !s) return null
  return { rgba: s.readRect({ x: -layer.offset.x, y: -layer.offset.y, w: doc.meta.width, h: doc.meta.height }), width: doc.meta.width, height: doc.meta.height }
}
let ref_: { rgba: Uint8ClampedArray; width: number; height: number } | null = null
// プレビュー用の縮小参照
let small: Uint8ClampedArray | null = null
function buildSmall() {
  ref_ = reference()
  small = null
  if (!ref_) return
  const out = new Uint8ClampedArray(pw * ph * 4)
  for (let y = 0; y < ph; y++) for (let x = 0; x < pw; x++) {
    const sx = Math.min(ref_.width - 1, Math.floor(x / scale)), sy = Math.min(ref_.height - 1, Math.floor(y / scale))
    const i = (sy * ref_.width + sx) * 4, o = (y * pw + x) * 4
    out[o] = ref_.rgba[i]!; out[o + 1] = ref_.rgba[i + 1]!; out[o + 2] = ref_.rgba[i + 2]!; out[o + 3] = ref_.rgba[i + 3]!
  }
  small = out
}
const rgb = computed(() => hexToRgb(hex.value) ?? { r: 0, g: 0, b: 0 })
function render() {
  const c = canvas.value
  if (!c || !small) return
  c.width = pw
  c.height = ph
  const ctx = c.getContext('2d')!
  const m = colorRange(small, { x: 0, y: 0, w: pw, h: ph }, { ...rgb.value, fuzziness: fuzziness.value, invert: invert.value })
  const img = ctx.createImageData(pw, ph)
  for (let y = 0; y < ph; y++) for (let x = 0; x < pw; x++) {
    let v = 0
    if (m) {
      const lx = x - m.rect.x, ly = y - m.rect.y
      if (lx >= 0 && ly >= 0 && lx < m.rect.w && ly < m.rect.h) v = m.data[ly * m.rect.w + lx]!
    }
    const o = (y * pw + x) * 4
    img.data[o] = v; img.data[o + 1] = v; img.data[o + 2] = v; img.data[o + 3] = 255
  }
  ctx.putImageData(img, 0, 0)
}
function pick(e: MouseEvent) {
  if (!small) return
  const r = canvas.value!.getBoundingClientRect()
  const x = Math.max(0, Math.min(pw - 1, Math.floor(((e.clientX - r.left) / r.width) * pw)))
  const y = Math.max(0, Math.min(ph - 1, Math.floor(((e.clientY - r.top) / r.height) * ph)))
  const i = (y * pw + x) * 4
  hex.value = rgbToHex({ r: small[i]!, g: small[i + 1]!, b: small[i + 2]! })
}
onMounted(() => {
  buildSmall()
  render()
})
watch([hex, fuzziness, invert], render)
watch(sampleAll, () => {
  buildSmall()
  render()
})
function ok() {
  void runCommand('selection.colorRange', { hex: hex.value, fuzziness: fuzziness.value, invert: invert.value, mode: ui.toolOptions.selectionMode, sampleAll: sampleAll.value })
  emit('close')
}
</script>

<template>
  <div class="modal-backdrop" @pointerdown.self="emit('close')">
    <div class="modal cr" @keydown.esc="emit('close')">
      <h2>{{ t('colorRange.title') }}</h2>
      <div class="body">
        <div class="preview">
          <canvas ref="canvas" :style="{ width: pw + 'px', height: ph + 'px' }" @click="pick" />
          <p class="muted small">{{ t('colorRange.pick') }}</p>
        </div>
        <div class="side">
          <label class="row"><input type="color" :value="hex" @input="hex = ($event.target as HTMLInputElement).value" /><input class="num mono" type="text" :value="hex" @change="hex = ($event.target as HTMLInputElement).value" /></label>
          <SliderField :label="t('colorRange.fuzziness')" :value="fuzziness" :min="0" :max="200" :step="1" @input="v => (fuzziness = v)" @change="v => (fuzziness = v)" />
          <label class="row"><input v-model="invert" type="checkbox" /><span>{{ t('colorRange.invert') }}</span></label>
          <label class="row"><input v-model="sampleAll" type="checkbox" /><span>{{ t('colorRange.sampleAll') }}</span></label>
        </div>
      </div>
      <div class="actions">
        <button @click="emit('close')">{{ t('dialog.cancel') }}</button>
        <button class="primary" @click="ok">{{ t('dialog.ok') }}</button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.body {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}
.preview canvas {
  display: block;
  border: 1px solid var(--line-strong);
  cursor: crosshair;
  image-rendering: pixelated;
}
.side {
  width: 240px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.small {
  font-size: 10px;
}
</style>
